import { captureException } from '@sentry/nextjs'
import { type useStatsigClient } from '@statsig/react-bindings'

import { type useClientGeoLocation } from 'components/app/clientGeoLocationContext'
import { getIsNonEmptyArray } from './getIsNonEmptyArray'
import { type AttributionData } from './attribution-helpers'
import { fetchSorts } from './sort/client/fetch-sorts'
import { SORT_DATA_MAP, SORT_MAP } from './sort/sort-map'
import { buildSortFetchData } from './sort/client/build-sort-fetch-data'
import type { SORT_DATA } from './sort/types/experimentation-types'
import type { SortParams, SortResponseInterface } from './sort/types'
import { fetchEntities } from './sort/client/fetch-entities'
import type { CompareCredit } from '../../types'

export async function handleFetchs(
  slugs: string[],
  sortData: SortParams,
  sortConfig: SORT_DATA | null,
) {
  const sortFetchData = buildSortFetchData(
    sortData,
    slugs,
    SORT_MAP,
    SORT_DATA_MAP,
    sortConfig,
  )

  return await Promise.allSettled([
    fetchEntities(slugs).catch((err) => {
      captureException(err)
      return Promise.reject(err)
    }),
    fetchSorts(sortFetchData).catch((err) => {
      captureException(err)
      return Promise.reject(err)
    }),
  ])
}

export function handleFetchResponses(
  entities: PromiseSettledResult<any>,
  sortedEntities: PromiseSettledResult<SortResponseInterface>,
) {
  let error = false
  let fetchedEntities = []
  if (sortedEntities.status === 'rejected' && entities.status === 'rejected') {
    // call to both sort and entities failed
    captureException(new Error('sort and entities call failed'))
    error = true
  } else if (
    sortedEntities.status === 'rejected' &&
    entities.status === 'fulfilled'
  ) {
    // call to sort failed, but entities succeeded
    fetchedEntities = entities.value.cards
  } else if (
    sortedEntities.status === 'fulfilled' &&
    entities.status === 'rejected'
  ) {
    // call to sort suceeded but entities failed
    captureException(new Error('entities call failed, but sort call succeeded'))
    error = true
  } else {
    // this shouldnt execute, but just in case
    captureException(new Error('fetch card error - this should never execute!'))
    error = true
  }
  return { fetchError: error, fetchedEntities }
}

export const EMPTY_RETURN: FetchCardsSlugReturn = {
  cards: [],
  isFetching: false,
  error: false,
}

export interface FetchCardsSlugReturn {
  cards: CompareCredit.Entity[]
  isFetching: boolean
  error: boolean
  sort?: string
}

export interface FetchCardsSlugComplex {
  slugs: [string] & string[]
  sortData: SortData
  sort: string
  adjustment: boolean | null
  attribution: AttributionData
  clientGeoLocation: ReturnType<typeof useClientGeoLocation>
  getDynamicConfig: ReturnType<typeof useStatsigClient>['getDynamicConfig']
  getExperiment: ReturnType<typeof useStatsigClient>['getExperiment']
  callback: (result: FetchCardsSlugReturn) => void
}

export type SortData = {
  categoryId: string
}

export type UseFetchCardsSlugCallback = (
  entities: CompareCredit.Entity[],
  sort?: string,
) => CompareCredit.Entity[]

export const getValidSlugs = (
  possibleSlugs: unknown,
): ([string] & string[]) | null => {
  if (Array.isArray(possibleSlugs)) {
    const validSlugs = possibleSlugs.flatMap((slug) =>
      typeof slug === 'string' && slug.length > 0 ? [slug] : [],
    )
    if (validSlugs.length === 1 && validSlugs[0] === null) {
      captureException(new Error('Slug is possibly undefined'), {
        contexts: { context: { slugs: validSlugs } },
      })
    }
    if (getIsNonEmptyArray(validSlugs)) {
      return validSlugs
    }
  }

  return null
}
